import React, { useState, useEffect, useCallback } from 'react';
import { naytiveAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './NaytiveFlags.css';

/* Naytive Apps → App Flags.
 *
 * Admin-controlled UI toggles for the Naytive v3 SPA. Each flag is a row in
 * naytive_feature_flags; the v3 app reads them live via /api/naytive/flags,
 * so flipping a toggle here changes user-facing behaviour without a deploy.
 * First flag: the C-Corp + India tax-residence FEMA subsidiary prompt. */

export default function NaytiveFlags() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await naytiveAPI.listFeatureFlags();
      setFlags(r.data?.items || []);
    } catch {
      setFlags([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const toggle = async (flag) => {
    const next = !flag.enabled;
    setSaving(flag.flag_key);
    // Optimistic flip.
    setFlags((prev) => prev.map((f) => (f.flag_key === flag.flag_key ? { ...f, enabled: next } : f)));
    try {
      await naytiveAPI.updateFeatureFlag(flag.flag_key, next);
      setToast(`${flag.label || flag.flag_key} ${next ? 'enabled' : 'disabled'}`);
    } catch {
      // Revert on failure.
      setFlags((prev) => prev.map((f) => (f.flag_key === flag.flag_key ? { ...f, enabled: !next } : f)));
      setToast('Could not save — try again');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">APP FLAGS</h1>
            <p className="page-subtitle">
              Toggle Naytive app behaviours live — the user app reads these instantly, no deploy needed.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="nf-empty">Loading flags…</div>
        ) : flags.length === 0 ? (
          <div className="nf-empty">No flags defined yet.</div>
        ) : (
          <div className="nf-list">
            {flags.map((f) => (
              <div className="nf-row" key={f.flag_key}>
                <div className="nf-row-main">
                  <div className="nf-row-title">
                    {f.label || f.flag_key}
                    <code className="nf-key">{f.flag_key}</code>
                  </div>
                  {f.description && <div className="nf-row-desc">{f.description}</div>}
                </div>
                <button
                  type="button"
                  className={`nf-switch${f.enabled ? ' on' : ''}`}
                  role="switch"
                  aria-checked={!!f.enabled}
                  disabled={saving === f.flag_key}
                  onClick={() => toggle(f)}
                  title={f.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                >
                  <span className="nf-switch-knob" />
                </button>
              </div>
            ))}
          </div>
        )}

        {toast && <div className="nf-toast">{toast}</div>}
      </main>
    </div>
  );
}
